import React, { type SyntheticEvent, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import LinkIcon from '@atlaskit/icon/core/link';
import type { LinkPickerProps as AkLinkPickerProps } from '@atlaskit/link-picker';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Pressable } from '@atlaskit/primitives';
import LinkUrl from '@atlaskit/smart-card/link-url';
import { CardSSR, type CardSSRProps } from '@atlaskit/smart-card/ssr';
import { token } from '@atlaskit/tokens';

import { LinkPickerContent } from './content';
import { messages } from './messages';

export type OnSubmitPayload = Parameters<AkLinkPickerProps['onSubmit']>[0];

type LinkPickerProps = {
	cloudId: string;
	handleClearLink: () => void;
	handleSelectLink: (selectedLink: OnSubmitPayload) => void;
	id: string;
	isDisabled?: boolean;
	link?: string;
	title?: string;
};

const styles = cssMap({
	disabled: {
		color: token('color.text.disabled'),
	},
	link: {
		maxWidth: '180px',
	},
	placeholder: {
		alignItems: 'center',
		gap: token('space.075'),
		flex: '1 0 0',
		color: token('color.text.subtle'),
	},
	selection: {
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
		paddingInline: token('space.050'),
	},
	trigger: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		height: '40px',
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
		paddingInline: token('space.200'),
	},
	triggerPressed: {
		backgroundColor: token('color.background.neutral.subtle.pressed'),
	},
	unresolvedLink: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

const getDefaultPayload = (link?: string, title?: string): OnSubmitPayload => ({
	url: link || '',
	displayText: null,
	title: title || null,
	meta: {
		inputMethod: 'manual',
	},
});

const Placeholder = () => (
	<Flex xcss={styles.placeholder}>
		<LinkIcon label="" />
		<FormattedMessage {...messages.popupPlaceholderText} />
	</Flex>
);

export const LinkPicker = ({
	id,
	cloudId,
	link,
	title,
	handleSelectLink,
	handleClearLink,
	isDisabled,
}: LinkPickerProps): React.JSX.Element => {
	const selectedLinkPayload = getDefaultPayload(link, title);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [isLinkFallbackError, setIsLinkFallbackError] = useState<boolean>(false);

	const onLinkClick: CardSSRProps['onClick'] = () => window.open(selectedLinkPayload.url, '_blank');

	const onResolve: CardSSRProps['onResolve'] = () => setIsLinkFallbackError(false);

	const onError: CardSSRProps['onError'] = ({ status }) =>
		setIsLinkFallbackError(status !== 'forbidden' && status !== 'unauthorized');

	const ResolvedLink = () => {
		return (
			<Box xcss={styles.link}>
				{!isLinkFallbackError && !isDisabled ? (
					<CardSSR
						appearance="inline"
						onClick={onLinkClick}
						onError={onError}
						onResolve={onResolve}
						truncateInline
						url={selectedLinkPayload.url}
					/>
				) : (
					<LinkUrl
						{...(!isDisabled && { href: selectedLinkPayload.url })}
						onClick={(e: SyntheticEvent) => e.stopPropagation()}
						target="_blank"
					>
						<Box xcss={cx(styles.unresolvedLink, isDisabled && styles.disabled)}>
							{selectedLinkPayload.url}
						</Box>
					</LinkUrl>
				)}
			</Box>
		);
	};

	const onLinkClear = (e: SyntheticEvent) => {
		e.stopPropagation();
		setIsLinkFallbackError(false);
		handleClearLink();
	};

	return (
		<SmartCardProvider>
			<Popup
				id={id}
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				placement="left"
				content={() => (
					<LinkPickerContent
						cloudId={cloudId}
						link={selectedLinkPayload}
						onLinkSelect={handleSelectLink}
						setIsOpen={setIsOpen}
					/>
				)}
				trigger={(triggerProps) => (
					<Pressable
						{...triggerProps}
						isDisabled={isDisabled}
						onClick={() => setIsOpen(!isOpen)}
						testId="link-picker-popup-button"
						style={{ border: `${token('border.width')} solid ${token('color.border')}` }}
						xcss={cx(styles.trigger, isOpen && styles.triggerPressed)}
					>
						{!!selectedLinkPayload.url ? (
							<Flex xcss={styles.selection}>
								<ResolvedLink />
								{!isDisabled && (
									<IconButton
										onClick={onLinkClear}
										appearance="subtle"
										spacing="compact"
										label={<FormattedMessage {...messages.clearIconLabel} />}
										icon={CrossIcon}
									/>
								)}
							</Flex>
						) : (
							<Placeholder />
						)}
					</Pressable>
				)}
				shouldRenderToParent={fg('should-render-to-parent-should-be-true-conf')}
			/>
		</SmartCardProvider>
	);
};
